import type { TableSlot } from '@durak/shared';
import { PlayingCard } from './PlayingCard';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface Props {
  slots: TableSlot[];
  onSlotClick?: (idx: number) => void;
  activeSlotIdx?: number | null;
  /** Optional ref-collecting map so the parent can hit-test drags against the
   *  slot elements. Elements (not cached rects): every card added to the
   *  flex-wrapped table shifts the existing slots, so rects must be measured
   *  at drop time. */
  slotRectsRef?: React.MutableRefObject<Map<number, HTMLElement>>;
  /** Optional ref to the table's outer element for transfer hit-testing. */
  tableRectRef?: React.MutableRefObject<HTMLElement | null>;
  /** Which slots are valid drop targets — used to highlight while dragging. */
  highlightSlots?: Set<number>;
  /** When true, render a visible "drop here to transfer" ghost slot. */
  showTransferZone?: boolean;
  /** When true, slot exit animation flies the cards upward toward the opponents
   *  row — used when an opponent takes the table. */
  exitToOpponent?: boolean;
  /** When true, the local viewer is the one taking — the wrapper fades quietly
   *  so the shared-layout animation moving each card into the hand is what's
   *  visible, not a slot-wrapper translation. */
  exitToLocal?: boolean;
}

export function Table({ slots, onSlotClick, activeSlotIdx, slotRectsRef, tableRectRef, highlightSlots, showTransferZone, exitToOpponent, exitToLocal }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!tableRectRef) return;
    tableRectRef.current = containerRef.current;
    return () => {
      tableRectRef.current = null;
    };
  }, [tableRectRef]);

  return (
    <div
      ref={containerRef}
      className={clsx(
        'flex flex-wrap justify-center items-center gap-3 sm:gap-4 px-2 sm:px-4 py-3 min-h-[124px] relative rounded-xl transition',
      )}
    >
      {slots.length === 0 && (
        <div className="flex items-center justify-center text-ink-200/60 text-sm italic font-display tracking-wide py-4">
          Waiting for the first attack…
        </div>
      )}
      <AnimatePresence>
        {slots.map((slot, idx) => (
          <Slot
            key={`${slot.attack.id}-${idx}`}
            idx={idx}
            slot={slot}
            onClick={() => onSlotClick?.(idx)}
            active={activeSlotIdx === idx}
            highlight={highlightSlots?.has(idx)}
            slotRectsRef={slotRectsRef}
            flyOnExit={exitToOpponent}
            quietExit={exitToLocal}
          />
        ))}
      </AnimatePresence>
      {showTransferZone && slots.length > 0 && <TransferZone />}
    </div>
  );
}

/**
 * Visible "drop here to transfer" placeholder appended after the played pairs.
 * It sits inside the table container, so framer-motion's table-hit-test
 * still classifies a drop on it as `{ kind: 'table' }` → transfer.
 */
function TransferZone() {
  return (
    <div
      className="table-slot flex items-center justify-center rounded-lg border-2 border-dashed border-accent/60 bg-accent/8 text-accent text-[11px] font-display uppercase tracking-[0.15em] select-none"
      style={{ pointerEvents: 'none' }}
      aria-hidden
    >
      Transfer
    </div>
  );
}

interface SlotProps {
  idx: number;
  slot: TableSlot;
  onClick?: () => void;
  active?: boolean;
  highlight?: boolean;
  slotRectsRef?: React.MutableRefObject<Map<number, HTMLElement>>;
  /** When true, exit animation flies the slot upward — used when an opponent
   *  takes the table. */
  flyOnExit?: boolean;
  /** When true, the wrapper just fades — the cards inside ride a shared
   *  layoutId animation into the local player's hand, so any wrapper
   *  translation would compete with that motion. */
  quietExit?: boolean;
}

function Slot({ idx, slot, onClick, active, highlight, slotRectsRef, flyOnExit, quietExit }: SlotProps) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!slotRectsRef) return;
    const map = slotRectsRef.current;
    if (ref.current) map.set(idx, ref.current);
    return () => {
      map.delete(idx);
    };
  }, [idx, slotRectsRef]);

  return (
    <motion.button
      ref={ref}
      type="button"
      onClick={onClick}
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={flyOnExit
        ? { y: -260, opacity: 0, scale: 0.6, rotate: -4 + (idx % 3) * 4, transition: { duration: 0.45, ease: [0.4, 0, 0.2, 1], delay: idx * 0.04 } }
        : quietExit
          ? { opacity: 0, transition: { duration: 0.35, ease: 'easeOut' } }
          : { y: 90, x: -140 + (idx % 3) * 70, opacity: 0, scale: 0.55, rotate: -10 + (idx % 4) * 7, transition: { duration: 0.55, ease: [0.4, 0, 0.2, 1], delay: idx * 0.05 } }}
      transition={{ type: 'tween', duration: 0.22, ease: 'easeOut' }}
      className={clsx(
        'table-slot rounded-md',
        active && 'ring-2 ring-accent shadow-glow',
        highlight && 'ring-2 ring-emerald-400/70 shadow-glow',
      )}
    >
      <PlayingCard card={slot.attack} layoutId={`card-${slot.attack.id}`} />
      {slot.defense && (
        <div className="defense-wrap">
          <PlayingCard card={slot.defense} layoutId={`card-${slot.defense.id}`} />
        </div>
      )}
    </motion.button>
  );
}
